/**
 * AUROS · Club de Beneficios
 * Interacciones de la landing abierta para clientes.
 * Sin dependencias externas.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-aeb-root]';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const reducedMotionQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia(REDUCED_MOTION)
      : null;

  const prefersReducedMotion = () => reducedMotionQuery?.matches === true;

  const emit = (root, name, detail = {}) => {
    const payload = {
      event: `auros_beneficios_${name}`,
      page_path: window.location.pathname,
      section_id: root?.dataset?.sectionId || '',
      ...detail,
    };

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (error) {
      // Analytics nunca debe bloquear la experiencia.
    }

    try {
      document.dispatchEvent(
        new CustomEvent('auros:beneficios', {
          detail: payload,
        })
      );
    } catch (error) {
      // Compatibilidad defensiva.
    }
  };

  const getFocusableField = (container) => {
    if (!container) return null;

    return (
      container.querySelector(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
      ) ||
      container.querySelector(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe'
      )
    );
  };

  const focusFormField = (container, preserveViewport = false) => {
    const field = getFocusableField(container);
    if (!field) return false;

    const x = window.scrollX;
    const y = window.scrollY;

    try {
      field.focus({ preventScroll: true });
    } catch (error) {
      try {
        field.focus();
      } catch (focusError) {
        return false;
      }
    }

    if (preserveViewport) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ left: x, top: y, behavior: 'auto' });
      });
    }

    return true;
  };

  const scrollToTarget = (root, target) => {
    if (!target) return;

    const reduceMotion = prefersReducedMotion();
    const rect = target.getBoundingClientRect();
    const absoluteTop = rect.top + window.scrollY;
    const offset = 18;
    const top = Math.max(0, absoluteTop - offset);

    window.scrollTo({
      top,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });

    if (target.matches('[id*="Form"], [data-aeb-form-card]')) {
      window.setTimeout(() => {
        focusFormField(target);
      }, reduceMotion ? 0 : 420);
    }
  };

  const initSmoothScroll = (root) => {
    root.querySelectorAll('[data-aeb-scroll]').forEach((link) => {
      if (link.dataset.aebScrollReady === 'true') return;
      link.dataset.aebScrollReady = 'true';

      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;

        const target = document.querySelector(href);
        if (!target) return;

        event.preventDefault();
        scrollToTarget(root, target);
      });
    });
  };

  const initFormFocusCta = (root) => {
    root.querySelectorAll('[data-aeb-focus-form]').forEach((button) => {
      if (button.dataset.aebFocusReady === 'true') return;
      button.dataset.aebFocusReady = 'true';

      button.addEventListener('click', () => {
        const formCard = root.querySelector('[data-aeb-form-card]');
        if (!formCard) return;

        if (focusFormField(formCard, true)) return;

        let attempts = 0;
        const retry = window.setInterval(() => {
          attempts += 1;
          if (focusFormField(formCard, true) || attempts >= 10) {
            window.clearInterval(retry);
          }
        }, 100);
      });
    });
  };

  const initReveal = (root) => {
    const items = Array.from(root.querySelectorAll('[data-aeb-reveal]'));
    if (!items.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);

          const clearDelay = () => {
            entry.target.style.transitionDelay = '';
          };

          entry.target.addEventListener('transitionend', clearDelay, { once: true });
          window.setTimeout(clearDelay, 500);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08,
      }
    );

    items.forEach((item, index) => {
      item.style.transitionDelay = `${Math.min(index % 4, 3) * 35}ms`;
      observer.observe(item);
    });
  };

  const initStickyCta = (root) => {
    const sticky = root.querySelector('[data-aeb-sticky]');
    const formSection = root.querySelector('[id*="AebForm-"]');

    if (!sticky || !formSection || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          sticky.classList.toggle('is-hidden', entry.isIntersecting);
        });
      },
      {
        threshold: 0.18,
      }
    );

    observer.observe(formSection);
  };

  const initTracking = (root) => {
    root.querySelectorAll('[data-aeb-track]').forEach((element) => {
      if (element.dataset.aebTrackReady === 'true') return;
      element.dataset.aebTrackReady = 'true';

      element.addEventListener('click', () => {
        emit(root, 'click', {
          action: element.dataset.aebTrack || 'unknown',
          label:
            element.textContent
              ?.trim()
              ?.replace(/\s+/g, ' ')
              ?.slice(0, 120) || '',
          href: element.getAttribute('href') || '',
        });
      });
    });
  };

  const initFormVisibility = (root) => {
    const formCard = root.querySelector('[data-aeb-form-card]');

    if (
      !formCard ||
      formCard.dataset.aebVisibilityReady === 'true' ||
      !('IntersectionObserver' in window)
    ) {
      return;
    }

    formCard.dataset.aebVisibilityReady = 'true';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          emit(root, 'form_view');
          observer.disconnect();
        });
      },
      {
        threshold: 0.35,
      }
    );

    observer.observe(formCard);
  };

  const initFormSignals = (root) => {
    const formCard = root.querySelector('[data-aeb-form-card]');
    if (!formCard || formCard.dataset.aebFormSignalsReady === 'true') return;

    formCard.dataset.aebFormSignalsReady = 'true';
    let started = false;

    const markStart = () => {
      if (started) return;
      started = true;
      emit(root, 'form_start');
    };

    formCard.addEventListener('focusin', markStart, true);
    formCard.addEventListener('input', markStart, true);

    formCard.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        emit(root, 'form_submit_attempt');
      },
      true
    );

    const successObserver = new MutationObserver(() => {
      const success = formCard.querySelector(
        '[role="status"], [data-testid*="success" i], [class*="success" i], [id*="success" i]'
      );

      if (!success) return;

      const text = success.textContent?.trim() || '';
      if (!text || success.dataset.aebSuccessTracked === 'true') return;

      const normalized = text.toLowerCase();
      const looksSuccessful =
        normalized.includes('gracias') ||
        normalized.includes('enviado') ||
        normalized.includes('registr') ||
        normalized.includes('suscri') ||
        normalized.includes('success');

      if (!looksSuccessful) return;

      success.dataset.aebSuccessTracked = 'true';
      emit(root, 'form_success');
      successObserver.disconnect();
    });

    successObserver.observe(formCard, {
      childList: true,
      subtree: true,
    });
  };


  const initRoot = (root) => {
    if (!root || root.dataset.aebInitialized === 'true') return;

    root.dataset.aebInitialized = 'true';
    root.classList.add('aeb--js');

    initSmoothScroll(root);
    initFormFocusCta(root);
    initReveal(root);
    initStickyCta(root);
    initTracking(root);
    initFormVisibility(root);
    initFormSignals(root);

    emit(root, 'view');
  };

  const initAll = (scope = document) => {
    if (!scope) return;

    if (scope.matches?.(ROOT_SELECTOR)) {
      initRoot(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), {
      once: true,
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();